import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Gift, Users, Trophy, Share2, Copy, Check, 
  MessageCircle, Mail, Twitter, ChevronRight,
  Crown, Star, Sparkles, Award
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralProgram = ({ token }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [shareContent, setShareContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [statsRes, leaderboardRes, shareRes] = await Promise.all([
        axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referral/leaderboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referral/share-content`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setShareContent(shareRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, templateName = null) => {
    try {
      await navigator.clipboard.writeText(text);
      if (templateName) {
        setCopiedTemplate(templateName);
        setTimeout(() => setCopiedTemplate(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareVia = (platform) => {
    if (!shareContent) return;
    
    const templates = shareContent.templates;
    let url = '';
    
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(templates.twitter)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(templates.whatsapp)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent(templates.email_subject)}&body=${encodeURIComponent(templates.email_body)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const getTierColor = (tier) => {
    const colors = {
      1: 'from-green-400 to-emerald-500',
      2: 'from-blue-400 to-indigo-500',
      3: 'from-purple-400 to-pink-500',
      4: 'from-orange-400 to-red-500',
      5: 'from-yellow-400 to-amber-500',
      6: 'from-cyan-400 to-blue-500'
    };
    return colors[tier] || 'from-gray-400 to-gray-500';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="referral-program">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-3xl p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Gift size={32} />
          <h1 className="text-3xl font-bold">{t('referral.title')}</h1>
        </div>
        <p className="text-orange-100 text-lg mb-6">
          Share NutriLens with friends and earn free Premium!
        </p>
        
        {/* Referral Code Card */}
        <div className="bg-white dark:bg-gray-800/20 backdrop-blur rounded-2xl p-6">
          <p className="text-sm text-orange-100 mb-2">Your Referral Code</p>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-white dark:bg-gray-800/30 rounded-xl px-6 py-4 font-mono text-2xl font-bold tracking-wider">
              {stats?.referral_code}
            </div>
            <button
              onClick={() => copyToClipboard(stats?.referral_code)}
              className="p-4 bg-white dark:bg-gray-800 text-orange-500 rounded-xl hover:bg-orange-50 transition-colors"
              data-testid="copy-code-btn"
            >
              {copied ? <Check size={24} /> : <Copy size={24} />}
            </button>
          </div>
          {copied && <p className="text-sm mt-2 text-orange-100">Copied to clipboard!</p>}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Users size={24} className="text-orange-500" />
            <span className="text-gray-500 dark:text-gray-400">Total Referrals</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.total_referrals || 0}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Check size={24} className="text-green-500" />
            <span className="text-gray-500 dark:text-gray-400">Completed</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.completed_referrals || 0}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Crown size={24} className="text-purple-500" />
            <span className="text-gray-500 dark:text-gray-400">Current Tier</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.current_tier || 0}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Star size={24} className="text-yellow-500" />
            <span className="text-gray-500 dark:text-gray-400">Days Earned</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.total_premium_days_earned || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
        {['overview', 'share', 'leaderboard', 'rewards'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all capitalize ${
              activeTab === tab 
                ? 'bg-white dark:bg-gray-800 text-orange-500 shadow-sm' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 p-6">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Progress to Next Tier */}
            {stats?.next_tier && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Progress to Next Tier</h3>
                <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{stats.tiers[stats.current_tier]?.badge_icon || '🌱'}</span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {stats.tiers[stats.current_tier]?.badge_name || 'Getting Started'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Current Tier</p>
                      </div>
                    </div>
                    <ChevronRight size={24} className="text-gray-400" />
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{stats.next_tier.badge_icon}</span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{stats.next_tier.badge_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Next Tier</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div 
                      className="absolute h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(100, ((stats.completed_referrals || 0) / stats.next_tier.referrals_required) * 100)}%` 
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                    {stats.referrals_to_next_tier} more referral{stats.referrals_to_next_tier !== 1 ? 's' : ''} to unlock 
                    <span className="font-semibold text-orange-500"> {stats.next_tier.reward_days} days free Premium!</span>
                  </p>
                </div>
              </div>
            )}

            {/* All Tiers */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Reward Tiers</h3>
              <div className="grid gap-4">
                {stats?.tiers?.map((tier, index) => (
                  <div 
                    key={tier.tier}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                      stats.completed_referrals >= tier.referrals_required
                        ? 'border-green-200 bg-green-50'
                        : stats.current_tier === index
                        ? 'border-orange-200 bg-orange-50'
                        : 'border-gray-100 bg-gray-50 dark:bg-gray-900'
                    }`}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getTierColor(tier.tier)} flex items-center justify-center text-2xl`}>
                      {tier.badge_icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 dark:text-white">{tier.badge_name}</p>
                        {stats.completed_referrals >= tier.referrals_required && (
                          <Check size={18} className="text-green-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{tier.referrals_required} referral{tier.referrals_required !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-500">
                        {tier.reward_days >= 365 
                          ? tier.reward_days >= 3650 
                            ? 'Lifetime' 
                            : `${Math.floor(tier.reward_days / 365)} year`
                          : tier.reward_days >= 30 
                            ? `${Math.floor(tier.reward_days / 30)} month${Math.floor(tier.reward_days / 30) > 1 ? 's' : ''}`
                            : `${tier.reward_days} days`
                        }
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">free Premium</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'share' && shareContent && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Share with Friends</h3>
            
            {/* Quick Share Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => shareVia('whatsapp')}
                className="flex items-center justify-center gap-2 p-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                data-testid="share-whatsapp"
              >
                <MessageCircle size={20} />
                WhatsApp
              </button>
              <button
                onClick={() => shareVia('twitter')}
                className="flex items-center justify-center gap-2 p-4 bg-blue-400 text-white rounded-xl hover:bg-blue-500 transition-colors"
                data-testid="share-twitter"
              >
                <Twitter size={20} />
                Twitter
              </button>
              <button
                onClick={() => shareVia('email')}
                className="flex items-center justify-center gap-2 p-4 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
                data-testid="share-email"
              >
                <Mail size={20} />
                Email
              </button>
              <button
                onClick={() => copyToClipboard(shareContent.referral_link)}
                className="flex items-center justify-center gap-2 p-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
                data-testid="copy-link"
              >
                <Copy size={20} />
                Copy Link
              </button>
            </div>

            {/* Share Templates */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Ready-to-use Messages</h4>
              
              {Object.entries({
                sms: { label: 'SMS / Text', icon: MessageCircle },
                instagram: { label: 'Instagram', icon: Share2 },
                twitter: { label: 'Twitter', icon: Twitter }
              }).map(([key, { label, icon: Icon }]) => (
                <div key={key} className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon size={18} className="text-gray-500 dark:text-gray-400" />
                      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(shareContent.templates[key], key)}
                      className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                    >
                      {copiedTemplate === key ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{shareContent.templates[key]}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Top Referrers</h3>
            <div className="space-y-3">
              {leaderboard.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Be the first to refer friends!</p>
              ) : (
                leaderboard.map((entry, index) => (
                  <div 
                    key={entry.user_id}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      entry.is_current_user 
                        ? 'bg-orange-50 border-2 border-orange-200' 
                        : 'bg-gray-50 dark:bg-gray-900'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700 dark:text-gray-300' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-200 text-gray-600 dark:text-gray-300'
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {entry.name}
                        {entry.is_current_user && <span className="text-orange-500 ml-2">(You)</span>}
                      </p>
                    </div>
                    <span className="text-2xl">{entry.badge}</span>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 dark:text-white">{entry.referral_count}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">referrals</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'rewards' && (
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Your Rewards</h3>
            {stats?.rewards_earned?.length === 0 ? (
              <div className="text-center py-12">
                <Gift size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-2">No rewards yet</p>
                <p className="text-sm text-gray-400">Refer friends to start earning free Premium!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.rewards_earned?.map((reward, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center text-white">
                      <Award size={24} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white">{reward.badge_name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Tier {reward.tier} unlocked</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">+{reward.reward_days} days</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Premium added</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Recent Referrals */}
            {stats?.recent_referrals?.length > 0 && (
              <div className="mt-8">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Recent Referrals</h4>
                <div className="space-y-2">
                  {stats.recent_referrals.map((ref, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <Users size={16} className="text-orange-500" />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{ref.referred_name}</span>
                      </div>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        ref.status === 'completed' 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-yellow-100 text-yellow-600'
                      }`}>
                        {ref.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralProgram;
